import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Table
} from 'reactstrap';
import { createCardInventoryApi, getCardSummaryApi, listOfficesApi, transferCardInventoryApi, listCardsByOfficeApi, deleteCardInventoryApi, createLostCardsApi, createDamagedCardsApi } from "../../services/Api";
import cogoToast from "cogo-toast";

const CardInventorySummary = ({ onInventoryChange }) => {
    const [summaryData, setSummaryData] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        office_id: 1,
        qty: 0,
        remarks: '',
        assigned_to_email: ''
    });

    const [offices, setOffices] = useState([]);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [createdData, setCreatedData] = useState(null);

    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [transferFormData, setTransferFormData] = useState({
        transfer_from_office: '',
        transfer_to_office: '',
        qty: 0,
        remarks: '',
        assigned_to_email: ''
    });
    const [transferSuccessModalOpen, setTransferSuccessModalOpen] = useState(false);
    const [transferSuccessData, setTransferSuccessData] = useState(null);

    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [officeHistoryData, setOfficeHistoryData] = useState([]);
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'lost' | 'damaged'
    const [dashboardMetrics, setDashboardMetrics] = useState({
        overall: 0,
        lost: 0,
        damaged: 0,
        available: 0
    });
    const [officeStats, setOfficeStats] = useState({});

    useEffect(() => {
        fetchSummary();
        fetchOffices();
    }, []);

    const fetchOffices = async () => {
        try {
            const res = await listOfficesApi();
            if (res.status === 200) {
                // Ensure data is array
                const data = res.data.data || res.data;
                if (Array.isArray(data)) {
                    setOffices(data);
                } else {
                    setOffices([]);
                }
            }
        } catch (err) {
        }
    };

    const fetchSummary = async () => {
        try {
            const res = await getCardSummaryApi();
            if (res.status === 200 && res.data.status === 10001) {
                const data = res.data.data;
                setSummaryData(data);
                fetchDashboardMetrics(data);
            }
        } catch (err) {
        }
    };

    const fetchDashboardMetrics = async (data) => {
        let totalLost = 0;
        let totalDamaged = 0;
        let totalOverall = 0;
        const newOfficeStats = {};

        for (const item of data) {
            totalOverall += parseInt(item.current_balance || 0);
            if (item.office?.id) {
                let officeLost = 0;
                let officeDamaged = 0;
                try {
                    const res = await listCardsByOfficeApi({ office_id: item.office.id });
                    if (res.status === 200 && res.data.status === 10001) {
                        // Iterate transactions
                        const transactions = res.data.data;
                        transactions.forEach(t => {
                            if (t.transaction_type === 'lost_cards') {
                                const qty = parseInt(t.qty_out || t.qty || 0);
                                totalLost += qty;
                                officeLost += qty;
                            } else if (t.transaction_type === 'damaged_cards') {
                                const qty = parseInt(t.qty_out || t.qty || 0);
                                totalDamaged += qty;
                                officeDamaged += qty;
                            }
                        });
                    }
                } catch (e) {
                }
                newOfficeStats[item.office.id] = { lost: officeLost, damaged: officeDamaged };
            }
        }
        setDashboardMetrics({
            overall: totalOverall,
            lost: totalLost,
            damaged: totalDamaged,
            available: totalOverall - (totalLost + totalDamaged)
        });
        setOfficeStats(newOfficeStats);
    };

    const handleSubmitInventory = async () => {
        try {
            if (!createFormData.qty || !createFormData.remarks || !createFormData.assigned_to_email) {
                cogoToast.warn("Please fill all required fields.");
                return;
            }

            let apiCall;
            if (modalMode === 'lost') {
                apiCall = createLostCardsApi;
            } else if (modalMode === 'damaged') {
                apiCall = createDamagedCardsApi;
            } else {
                apiCall = createCardInventoryApi;
            }

            const res = await apiCall(createFormData);
            if (res.status === 200 && (res.data.status === 10001 || res.data.code === 200)) {
                cogoToast.success(`${modalMode === 'create' ? 'Inventory created' : modalMode === 'lost' ? 'Lost report submitted' : 'Damage report submitted'} successfully!`);
                setCreateModalOpen(false);
                setCreateFormData({
                    office_id: 1,
                    qty: 0,
                    remarks: '',
                    assigned_to_email: ''
                });

                // Show success details
                setCreatedData(res.data.data);
                setSuccessModalOpen(true);

                fetchSummary();
                if (onInventoryChange) {
                    onInventoryChange();
                }
            } else {
                cogoToast.error(res.data.message || "Operation failed.");
            }
        } catch (err) {
            cogoToast.error("Error submitting inventory.");
        }
    };

    const handleTransferInventory = async () => {
        try {
            if (!transferFormData.transfer_from_office || !transferFormData.transfer_to_office || !transferFormData.qty || !transferFormData.assigned_to_email) {
                cogoToast.warn("Please fill all required fields.");
                return;
            }

            // if (transferFormData.transfer_from_office === transferFormData.transfer_to_office) {
            //     cogoToast.warn("Transfer from and to offices must be different.");
            //     return;
            // }

            const res = await transferCardInventoryApi(transferFormData);
            if (res.status === 200 && (res.data.status === 10002 || res.data.code === 200 || res.data.status === 10001)) {
                // Expanded check to capture success 10001
                if (res.data.status === 10001 || res.data.code === 200 || res.data.message === 'Transfer successful' || res.data.status === 'success') {
                    cogoToast.success("Inventory transferred successfully!");
                    setTransferModalOpen(false);
                    setTransferFormData({
                        transfer_from_office: '',
                        transfer_to_office: '',
                        qty: 0,
                        remarks: '',
                        assigned_to_email: ''
                    });

                    // Show success details
                    setTransferSuccessData(res.data.data);
                    setTransferSuccessModalOpen(true);

                    fetchSummary();
                    if (onInventoryChange) onInventoryChange();
                } else {
                    cogoToast.error(res.data.message || "Transfer failed.");
                }
            } else {
                cogoToast.error(res.data.message || "Transfer failed.");
            }
        } catch (err) {
            cogoToast.error("Error processing transfer.");
        }
    };

    const handleViewHistory = async (office) => {
        if (!office?.id) return;
        setSelectedOffice(office);
        try {
            const res = await listCardsByOfficeApi({ office_id: office.id });
            if (res.status === 200 && res.data.status === 10001) {
                setOfficeHistoryData(res.data.data);
                setHistoryModalOpen(true);
            } else {
                cogoToast.error(res.data.message || "Failed to fetch history.");
            }
        } catch (err) {
            cogoToast.error("Error fetching history.");
        }
    };

    const handleDeleteInventory = async (signature) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            const res = await deleteCardInventoryApi({ signature });
            if (res.status === 200 && res.data.status === 10001) {
                cogoToast.success("Record deleted successfully");
                // Refresh list if offset/office known
                if (selectedOffice) {
                    handleViewHistory(selectedOffice);
                }
                fetchSummary();
            } else {
                cogoToast.error(res.data.message || "Delete failed.");
            }
        } catch (err) {
            cogoToast.error("Error deleting record.");
        }
    };

    return (
        <>
            {/* Dashboard Metrics Section */}
            <div className="mb-4">
                <Row>
                    <Col md={3}>
                        <Card className="text-white bg-primary">
                            <CardBody className="text-center">
                                <h3 className="text-white">Overall Cards</h3>
                                <h2 className="display-4 text-white font-weight-bold">{dashboardMetrics.overall}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-white bg-success">
                            <CardBody className="text-center">
                                <h3 className="text-white">Available Balance</h3>
                                <h2 className="display-4 text-white font-weight-bold">{dashboardMetrics.available}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-white bg-danger">
                            <CardBody className="text-center">
                                <h3 className="text-white">Damaged Cards</h3>
                                <h2 className="display-4 text-white font-weight-bold">{dashboardMetrics.damaged}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-white bg-warning">
                            <CardBody className="text-center">
                                <h3 className="text-white">Lost Cards</h3>
                                <h2 className="display-4 text-white font-weight-bold">{dashboardMetrics.lost}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Summary Section */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4>Inventory Summary</h4>
                    <div className="d-flex gap-2">
                        <Button
                            color="info"
                            className="mr-2"
                            onClick={() => setTransferModalOpen(true)}
                        >
                            Transfer Inventory
                        </Button>
                        <Button
                            color="danger"
                            className="mr-2"
                            onClick={() => { setModalMode('lost'); setCreateModalOpen(true); }}
                        >
                            Report Lost
                        </Button>
                        <Button
                            color="danger"
                            className="mr-2"
                            onClick={() => { setModalMode('damaged'); setCreateModalOpen(true); }}
                        >
                            Report Damaged
                        </Button>
                        <Button
                            color="success"
                            onClick={() => { setModalMode('create'); setCreateModalOpen(true); }}
                        >
                            Create Inventory
                        </Button>
                    </div>
                </div>

                <Row>
                    {summaryData.map((item, index) => (
                        <Col md={3} key={index}>
                            <Card
                                className="mb-3"
                                style={{ borderLeft: '4px solid #0056b3', cursor: 'pointer' }}
                                onClick={() => handleViewHistory(item.office)}
                            >
                                <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <CardTitle tag="h5" className="mb-0">{item.office?.office_name || 'Unknown Office'}</CardTitle>
                                    </div>
                                    <CardText>
                                        <div><strong>Total In:</strong> {item.total_in}</div>
                                        <div><strong>Total Out:</strong> {item.total_out}</div>
                                        <div><strong>Current Balance:</strong> {item.current_balance}</div>
                                        <div className="text-warning"><strong>Lost:</strong> {officeStats[item.office?.id]?.lost || 0}</div>
                                        <div className="text-danger"><strong>Damaged:</strong> {officeStats[item.office?.id]?.damaged || 0}</div>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>

            {/* Create Inventory Modal */}
            <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(false)}>
                <ModalHeader toggle={() => setCreateModalOpen(false)}>
                    {modalMode === 'create' ? 'Create Card Inventory' : modalMode === 'lost' ? 'Report Lost Cards' : 'Report Damaged Cards'}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Office</Label>
                            <Input
                                type="select"
                                value={createFormData.office_id}
                                onChange={(e) => setCreateFormData({ ...createFormData, office_id: e.target.value })}
                            >
                                <option value="">Select Office</option>
                                {offices.map(office => (
                                    <option key={office.id} value={office.id}>{office.office_name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Quantity</Label>
                            <Input
                                type="number"
                                value={createFormData.qty}
                                onChange={(e) => setCreateFormData({ ...createFormData, qty: e.target.value })}
                                placeholder="Enter quantity"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Remarks</Label>
                            <Input
                                type="text"
                                value={createFormData.remarks}
                                onChange={(e) => setCreateFormData({ ...createFormData, remarks: e.target.value })}
                                placeholder="Enter remarks"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Assigned To (Email)</Label>
                            <Input
                                type="email"
                                value={createFormData.assigned_to_email}
                                onChange={(e) => setCreateFormData({ ...createFormData, assigned_to_email: e.target.value })}
                                placeholder="Enter email"
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button color={modalMode === 'damaged' ? 'danger' : modalMode === 'lost' ? 'warning' : 'primary'} onClick={handleSubmitInventory}>
                        {modalMode === 'create' ? 'Create' : 'Report'}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Success Details Modal */}
            <Modal isOpen={successModalOpen} toggle={() => setSuccessModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setSuccessModalOpen(false)} className="bg-success text-white">
                    Inventory Created Successfully
                </ModalHeader>
                <ModalBody>
                    {createdData && (
                        <div className="p-3">
                            <div className="text-center mb-4">
                                <div className="avatar avatar-xl mb-2 bg-green-lt rounded-circle">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="44" height="44" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M5 12l5 5l10 -10" />
                                    </svg>
                                </div>
                                <h3>Success!</h3>
                                <p className="text-muted">The card inventory has been successfully created.</p>
                            </div>

                            <Card className="mb-3">
                                <CardBody>
                                    <Table bordered striped responsive>
                                        <tbody>
                                            <tr>
                                                <th style={{ width: '30%' }}>Office</th>
                                                <td>{createdData.office?.office_name}</td>
                                            </tr>
                                            <tr>
                                                <th>Quantity Added</th>
                                                <td className="text-success font-weight-bold">+{createdData.qty_in}</td>
                                            </tr>
                                            <tr>
                                                <th>Transaction Type</th>
                                                <td>{createdData.transaction_type}</td>
                                            </tr>
                                            <tr>
                                                <th>Remarks</th>
                                                <td>{createdData.remarks}</td>
                                            </tr>
                                            <tr>
                                                <th>Assigned To</th>
                                                <td>{createdData.assigned_to_email}</td>
                                            </tr>
                                            <tr>
                                                <th>Created By</th>
                                                <td>{createdData.creator?.name} ({createdData.creator?.email})</td>
                                            </tr>
                                            <tr>
                                                <th>Date</th>
                                                <td>{new Date(createdData.created_at).toLocaleString()}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setSuccessModalOpen(false)}>Close</Button>
                </ModalFooter>
            </Modal>
            {/* Transfer Inventory Modal */}
            <Modal isOpen={transferModalOpen} toggle={() => setTransferModalOpen(false)}>
                <ModalHeader toggle={() => setTransferModalOpen(false)}>Transfer Card Inventory</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Transfer From Office</Label>
                            <Input
                                type="select"
                                value={transferFormData.transfer_from_office}
                                onChange={(e) => setTransferFormData({ ...transferFormData, transfer_from_office: e.target.value })}
                            >
                                <option value="">Select Source Office</option>
                                {offices.map(office => (
                                    <option key={office.id} value={office.id}>{office.office_name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Transfer To Office</Label>
                            <Input
                                type="select"
                                value={transferFormData.transfer_to_office}
                                onChange={(e) => setTransferFormData({ ...transferFormData, transfer_to_office: e.target.value })}
                            >
                                <option value="">Select Destination Office</option>
                                {offices.map(office => (
                                    <option key={office.id} value={office.id}>{office.office_name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Quantity</Label>
                            <Input
                                type="number"
                                value={transferFormData.qty}
                                onChange={(e) => setTransferFormData({ ...transferFormData, qty: e.target.value })}
                                placeholder="Enter quantity"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Remarks</Label>
                            <Input
                                type="text"
                                value={transferFormData.remarks}
                                onChange={(e) => setTransferFormData({ ...transferFormData, remarks: e.target.value })}
                                placeholder="Enter remarks"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Assigned To (Email)</Label>
                            <Input
                                type="email"
                                value={transferFormData.assigned_to_email}
                                onChange={(e) => setTransferFormData({ ...transferFormData, assigned_to_email: e.target.value })}
                                placeholder="Enter email"
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setTransferModalOpen(false)}>Cancel</Button>
                    <Button color="info" onClick={handleTransferInventory}>Transfer</Button>
                </ModalFooter>
            </Modal>

            {/* Office History Modal */}
            <Modal isOpen={historyModalOpen} toggle={() => setHistoryModalOpen(false)} size="xl">
                <ModalHeader toggle={() => setHistoryModalOpen(false)}>
                    Transaction History - {selectedOffice?.office_name}
                </ModalHeader>
                <ModalBody>
                    <Table bordered striped responsive hover>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Transaction</th>
                                <th>Qty In</th>
                                <th>Qty Out</th>
                                <th>Remarks</th>
                                <th>Assigned To</th>
                                <th>Performed By</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {officeHistoryData && officeHistoryData.length > 0 ? (
                                officeHistoryData.map((record, index) => (
                                    <tr key={index}>
                                        <td>{new Date(record.created_at).toLocaleString()}</td>
                                        <td>{record.transaction_type}</td>
                                        <td className={record.qty_in > 0 ? "text-success" : ""}>{record.qty_in > 0 ? `+${record.qty_in}` : '-'}</td>
                                        <td className={record.qty_out > 0 ? "text-danger" : ""}>{record.qty_out > 0 ? `-${record.qty_out}` : '-'}</td>
                                        <td>{record.remarks}</td>
                                        <td>{record.assigned_to_email}</td>
                                        <td>{record.creator?.name}</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                color="danger"
                                                outline
                                                onClick={() => handleDeleteInventory(record.signature)}
                                                disabled={!record.signature}
                                                title="Delete Record"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash" width="16" height="16" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <line x1="4" y1="7" x2="20" y2="7" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                    <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                                    <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                                </svg>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center">No history found for this office.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setHistoryModalOpen(false)}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Transfer Success Details Modal */}
            <Modal isOpen={transferSuccessModalOpen} toggle={() => setTransferSuccessModalOpen(false)} size="xl">
                <ModalHeader toggle={() => setTransferSuccessModalOpen(false)} className="bg-info text-white">
                    Transfer Successful
                </ModalHeader>
                <ModalBody>
                    {transferSuccessData && (
                        <div className="p-3">
                            <div className="text-center mb-4">
                                <div className="avatar avatar-xl mb-2 bg-blue-lt rounded-circle">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrows-left-right" width="44" height="44" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <line x1="21" y1="14" x2="3" y2="14" />
                                        <path d="M6 10l-3 4l3 4" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                        <path d="M18 6l3 4l-3 4" />
                                    </svg>
                                </div>
                                <h3>Transfer Complete!</h3>
                                <p className="text-muted">The card inventory has been successfully transferred.</p>
                            </div>

                            <Row>
                                <Col md={6}>
                                    <Card className="mb-3 border-danger">
                                        <CardBody>
                                            <CardTitle tag="h5" className="text-danger">Source: {transferSuccessData.from_office_record?.office?.office_name}</CardTitle>
                                            <Table bordered striped responsive size="sm">
                                                <tbody>
                                                    <tr>
                                                        <th>Qty Out</th>
                                                        <td className="text-danger font-weight-bold">-{transferSuccessData.from_office_record?.qty_out}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Remarks</th>
                                                        <td>{transferSuccessData.from_office_record?.remarks}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="mb-3 border-success">
                                        <CardBody>
                                            <CardTitle tag="h5" className="text-success">Destination: {transferSuccessData.to_office_record?.office?.office_name}</CardTitle>
                                            <Table bordered striped responsive size="sm">
                                                <tbody>
                                                    <tr>
                                                        <th>Qty In</th>
                                                        <td className="text-success font-weight-bold">+{transferSuccessData.to_office_record?.qty_in}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Assigned To</th>
                                                        <td>{transferSuccessData.to_office_record?.assigned_to_email}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            <div className="mt-3 text-center text-muted">
                                <small>Transfer performed by {transferSuccessData.from_office_record?.creator?.name} on {new Date(transferSuccessData.from_office_record?.created_at).toLocaleString()}</small>
                            </div>

                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setTransferSuccessModalOpen(false)}>Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CardInventorySummary;
